import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Plus, Sparkles, ArrowRight, Rocket, Activity, Cpu, Clock, Gauge,
  Trash2, Loader2, Terminal, ExternalLink, Zap, CheckCircle2,
} from "lucide-react";
import api from "../lib/api";
import { TopBar } from "../components/TopBar";
import { ModelPicker } from "../components/ModelPicker";

const STATUS = {
  ready: { label: "Ready", cls: "bg-emerald-500/15 text-emerald-400", dot: "bg-emerald-400" },
  building: { label: "Building", cls: "bg-amber-500/15 text-amber-400", dot: "bg-amber-400 animate-pulse-glow" },
  error: { label: "Error", cls: "bg-rose-500/15 text-rose-400", dot: "bg-rose-400" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [idea, setIdea] = useState("");
  const [creating, setCreating] = useState(false);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const pendingHandled = useRef(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/projects"), api.get("/stats")]);
      setProjects(p.data);
      setStats(s.data);
    } catch { /* ignore */ }
  }, []);

  const createProject = useCallback(async (ideaText) => {
    if (!ideaText?.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/projects", { idea: ideaText.trim(), model });
      toast.success("Build started — your AI team is on it");
      setIdea("");
      navigate(`/project/${res.data.project_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start build");
      setCreating(false);
    }
  }, [navigate, model]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pending = localStorage.getItem("nexora_pending_idea");
    if (pending && !pendingHandled.current) {
      pendingHandled.current = true;
      localStorage.removeItem("nexora_pending_idea");
      createProject(pending);
    }
  }, [createProject]);

  // poll while any project is building
  useEffect(() => {
    if (!projects.some((p) => p.status === "building")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [projects, load]);

  const statCards = stats ? [
    { label: "Projects", value: stats.total_projects, icon: LayoutIcon, color: "text-indigo-400" },
    { label: "Ready", value: Math.max(stats.total_projects - stats.building, 0), icon: CheckCircle2, color: "text-emerald-400" },
    { label: "Building", value: stats.building, icon: Loader2, color: "text-amber-400" },
    { label: "Deployments", value: stats.deployments, icon: Rocket, color: "text-cyan-400" },
    { label: "Plan", value: (stats.plan || "free"), icon: Zap, color: "text-violet-400" },
  ] : [];

  return (
    <div className="min-h-screen">
      <TopBar />
      <div className="max-w-[1600px] mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold tracking-tight">Your workspace</h1>
          <p className="text-slate-400 mt-1">Describe an idea and your AI engineering team builds it end-to-end.</p>
        </div>

        {/* Create bar */}
        <div className="glass rounded-2xl p-2 glow-sm mb-8">
          <div className="flex items-center gap-2 px-3 pt-2 text-xs font-mono text-slate-500">
            <Terminal className="h-3.5 w-3.5" /> What do you want to build?
          </div>
          <div className="flex items-end gap-3 p-2">
            <textarea
              data-testid="dashboard-idea-input"
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) createProject(idea); }}
              placeholder="Build me a modern CRM for a real estate agency with a pipeline board…"
              rows={2}
              className="flex-1 bg-transparent resize-none outline-none px-2 py-1 placeholder:text-slate-600"
            />
            <ModelPicker value={model} onChange={setModel} align="right" />
            <button data-testid="dashboard-create-button" disabled={creating || !idea.trim()} onClick={() => createProject(idea)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 transition-colors font-semibold text-sm">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Build
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
          {statCards.map((s) => (
            <div key={s.label} className="glass rounded-xl p-5 card-hover">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-slate-500">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="font-heading text-2xl font-bold capitalize">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Projects */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-semibold">Projects</h2>
        </div>

        {projects.length === 0 ? (
          <div className="glass rounded-2xl p-16 text-center">
            <div className="h-14 w-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-5">
              <Zap className="h-6 w-6 text-indigo-400" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">No projects yet</h3>
            <p className="text-slate-400">Describe your first idea above to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p, i) => {
              const st = STATUS[p.status] || STATUS.building;
              return (
                <motion.div key={p.project_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  data-testid={`project-card-${p.project_id}`}
                  onClick={() => navigate(`/project/${p.project_id}`)}
                  className="glass rounded-2xl p-6 card-hover cursor-pointer group">
                  <div className="flex items-start justify-between mb-4">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-full ${st.cls}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {st.label}
                    </span>
                    <button data-testid={`project-delete-${p.project_id}`}
                      onClick={async (e) => { e.stopPropagation(); await api.delete(`/projects/${p.project_id}`); toast.success("Project deleted"); load(); }}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <h3 className="font-heading text-lg font-semibold mb-1 truncate">{p.name}</h3>
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4 min-h-[40px]">{p.tagline || p.idea}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1 text-sm text-indigo-400 group-hover:gap-2 transition-all">Open <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function LayoutIcon(props) {
  return <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" /></svg>;
}
